import { createClient } from '@supabase/supabase-js'

// Supabase admin client - environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseServiceKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY

if (!supabaseServiceKey) {
  console.error('VITE_SUPABASE_SERVICE_ROLE_KEY is not set. Admin operations will fail due to RLS policies.')
}

// Create admin client with service role key
export const supabaseAdmin = createClient(supabaseUrl, supabaseServiceKey, {
  auth: {
    autoRefreshToken: false,
    persistSession: false
  }
})

// Page content management functions
export const getPageFromSupabase = async (slug: string) => {
  try {
    console.log('Fetching page from Supabase with slug:', slug);
    
    const { data, error } = await supabaseAdmin
      .from('pages')
      .select('*')
      .eq('slug', slug)
      .single()

    if (error) {
      console.error('Error fetching page from Supabase:', error)
      return null
    }

    console.log('Page fetched successfully:', data);
    return data
  } catch (error) {
    console.error('Error in getPageFromSupabase:', error)
    return null
  }
}

export const getAllPagesFromSupabase = async () => {
  try {
    console.log('Fetching all pages from Supabase');
    
    const { data, error } = await supabaseAdmin
      .from('pages')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching pages from Supabase:', error)
      return []
    }

    console.log(`Fetched ${data.length} pages from Supabase`);
    return data
  } catch (error) {
    console.error('Error in getAllPagesFromSupabase:', error)
    return []
  }
}

export const savePageToSupabase = async (page: any) => {
  try {
    console.log('Saving page to Supabase:', page);
    
    // If it's a new page (no ID), remove the ID field to let Supabase generate one
    const pageData = { ...page };
    if (!pageData.id) {
      delete pageData.id;
      console.log('Creating new page, removed ID field');
    }
    
    // Add created_at if it's a new page
    if (!pageData.created_at) {
      pageData.created_at = new Date().toISOString();
      console.log('Added created_at timestamp:', pageData.created_at);
    }
    
    // Always update updated_at
    pageData.updated_at = new Date().toISOString();
    console.log('Set updated_at timestamp:', pageData.updated_at);
    
    // If publishing for the first time, set published_at
    if (pageData.is_published && !pageData.published_at) {
      pageData.published_at = new Date().toISOString();
      console.log('Set published_at timestamp:', pageData.published_at);
    }
    
    console.log('Final page data to save:', pageData);
    
    const { data, error } = await supabaseAdmin
      .from('pages')
      .upsert([pageData])
      .select()
      .single()

    if (error) {
      console.error('Error saving page to Supabase:', error)
      return null
    }

    console.log('Page saved successfully:', data);
    return data
  } catch (error) {
    console.error('Error in savePageToSupabase:', error)
    return null
  }
}

export const deletePageFromSupabase = async (id: string) => {
  try {
    console.log('Deleting page from Supabase with ID:', id);
    
    const { error } = await supabaseAdmin
      .from('pages')
      .delete()
      .eq('id', id)

    if (error) {
      console.error('Error deleting page from Supabase:', error)
      return false
    }

    console.log('Page deleted successfully');
    return true
  } catch (error) {
    console.error('Error in deletePageFromSupabase:', error)
    return false
  }
}